<script setup lang="ts">
/**
 * [#e03fdbc2] Окно первого визита: сообщает про Яндекс Метрику, даёт её отключить и выбрать язык.
 * Открыть снова можно из подвала.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { PUBLIC_LOCALES, localizedPath, splitLocale, type PublicLocale } from '@/seo/site';
import { getConsent, setConsent, startMetrika, type Consent } from './metrika';
import { usePublicPage } from './usePublicPage';

const LABELS: Record<PublicLocale, string> = { en: 'English', ru: 'Русский', uz: "O'zbekcha", es: 'Español' };

const { locale, more, to } = usePublicPage();
const route = useRoute();
const visible = ref(false);

const languages = computed(() =>
  PUBLIC_LOCALES.map((code) => ({ code, label: LABELS[code], href: localizedPath(splitLocale(route.path).path, code) })),
);

function pick(code: PublicLocale): void {
  try {
    localStorage.setItem('jan-mdm:lang', code);
  } catch {
    /* приватный режим: язык всё равно задаёт адрес */
  }
}

function choose(value: Consent): void {
  const wasOff = getConsent() === 'necessary';
  setConsent(value);
  visible.value = false;
  if (value === 'all') startMetrika();
  // Счётчик нельзя выключить на открытой странице, перезагрузка запускает её уже без него.
  else if (!wasOff) window.location.reload();
}

const open = () => (visible.value = true);

onMounted(() => {
  window.addEventListener('lp-consent-open', open);
  if (!getConsent()) setTimeout(open, 1500);
});
onUnmounted(() => window.removeEventListener('lp-consent-open', open));
</script>

<template>
  <Transition name="lp-notice">
    <aside v-if="visible" class="lp-notice" :aria-label="more.notice.title">
      <h2>{{ more.notice.title }}</h2>
      <p>{{ more.notice.text }} <RouterLink :to="to('/privacy')" class="lp-consent">{{ more.privacy.title }}</RouterLink></p>
      <ul :aria-label="more.notice.language">
        <li v-for="l in languages" :key="l.code">
          <a :href="l.href" :hreflang="l.code" :aria-current="l.code === locale ? 'true' : undefined" @click="pick(l.code)">{{ l.label }}</a>
        </li>
      </ul>
      <div class="lp-notice-actions">
        <button type="button" class="lp-btn" @click="choose('necessary')">{{ more.notice.necessary }}</button>
        <button type="button" class="lp-btn lp-btn--primary" @click="choose('all')">{{ more.notice.accept }}</button>
      </div>
    </aside>
  </Transition>
</template>
